import {
  Component,
  input,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';

import {
  AwDividerComponent,
  AwMetricCardComponent,
  AwSpinnerComponent,
  AwToastComponent,
  MetricCardAction,
  MetricCardData,
  MetricCardInformation,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';

import { Router } from '@angular/router';

import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import {
  Customer,
  Integration,
  ParentIntegration,
} from '../../../../api/models';
import { CustomerService } from '../../../../core/services/customer.service';
import { IntegrationService } from '../../../../core/services/integration.service';
import { Counter } from '../../../../types/types';

import {
  Organization,
  UserInfo,
  UserService,
} from '../../../../core/services/user.service';
import { EmployeeService } from '../../../../core/services/employee.service';
import { ParentIntegrationService } from '../../../../core/services/parent-integration.service';

@Component({
    selector: 'app-admin-dashboard',
    imports: [
    AwDividerComponent,
    AwMetricCardComponent,
    CdkDrag,
    CdkDropList,
    AwToastComponent,
    AwSpinnerComponent
],
    templateUrl: './admin-dashboard.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './admin-dashboard.component.scss'
})
export class AdminDashboardComponent implements OnInit {
  constructor(
    private router: Router,
    private customerService: CustomerService,
    private integrationService: IntegrationService,
    private userService: UserService,
    private employeeService: EmployeeService,
    private parentIntegrationService: ParentIntegrationService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  isLoading: boolean = false;

  allowDrag = input<boolean>(true);

  integrationsCardData = signal<MetricCardData[]>([]);
  customerCardData = signal<MetricCardData[]>([]);
  employeeCardData = signal<MetricCardData[]>([]);
  parentIntegrationsCardData = signal<MetricCardData[]>([]);

  cards: any[] = [];

  organization: Organization | null = null;
  organizationId: string | null = null;
  user: UserInfo | null = null;

  private countDataItems<T extends { enabled?: boolean }>(items: T[]): Counter {
    return (items ?? []).reduce(
      (acc, item) => {
        acc.total++;
        if (item.enabled) {
          acc.active++;
        } else {
          acc.disabled++;
        }
        return acc;
      },
      { total: 0, active: 0, disabled: 0 }
    );
  }

  private fetchCustomersFromIds(customerIds: string[]): Observable<Customer[]> {
    if (!customerIds.length) return of([]);

    return forkJoin(
      customerIds.map(id =>
        this.customerService.getCustomerById({ organizationId: id })
          .pipe(catchError(() => of(null)))
      )
    ).pipe(
      map(customers => customers.filter((c): c is Customer => c !== null))
    );
  }

  private fetchIntegrationsFromIds(integrationIds: string[]): Observable<Integration[]> {
    if (!integrationIds.length) return of([]);

    return forkJoin(
      integrationIds.map(id =>
        this.integrationService.getIntegrationById({ organizationId: id })
          .pipe(catchError(() => of(null)))
      )
    ).pipe(
      map(integrations => integrations.filter((i): i is Integration => i !== null))
    );
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }
  ngOnInit(): void {
    this.isLoading = true;

    this.organization = this.userService.getOrganization();
    this.user = this.userService.getUserInfo();
    this.organizationId = this.user?.organizationId ?? null;

    let integrations: Observable<any> = of([]);
    let customers: Observable<any> = of([]);
    let employees: Observable<any> = of([]);
    let parentIntegrations: Observable<any> = of([]);

    if (this.organization === 'assetworks-employee') {
      customers = this.customerService.getAllCustomers().pipe(
        catchError((error) => {
          this.buildToastMessage('Failed to fetch customer data', 'error');
          return of([]);
        })
      );

      integrations = this.integrationService.getAlIntegrations().pipe(
        catchError((error) => {
          this.buildToastMessage('Failed to fetch third-party integration data', 'error');
          return of([]);
        })
      );

      employees = this.employeeService.getAllEmployees().pipe(
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          this.buildToastMessage('Failed to fetch third-party employee data', 'error');
          return of([]);
        })
      );

      parentIntegrations = this.parentIntegrationService.getAllParentIntegrations().pipe(
        catchError((error) => {
          this.buildToastMessage('Failed to fetch third-party data', 'error');
          return of([]);
        })
      );
    } else if (this.organization === 'integration-employee') {
      const dataSource = this.parentIntegrationService
        .getParentIntegrationById({ organizationId: this.organizationId ?? '' })
        .pipe(
          switchMap((parentIntegration) => {
            if (!parentIntegration?.integrationIds?.length) {
              return of({
                integrations: [] as Integration[],
                customers: [] as Customer[],
                parentIntegration: parentIntegration || null
              });
            }

            return this.fetchIntegrationsFromIds(parentIntegration.integrationIds).pipe(
              switchMap(integrations => {
                const allCustomerIds = new Set<string>();
                integrations.forEach(integration =>
                  (integration.customerIds || []).forEach(id => allCustomerIds.add(id))
                );

                return this.fetchCustomersFromIds(Array.from(allCustomerIds)).pipe(
                  map(customers => ({ integrations, customers, parentIntegration }))
                );
              })
            );
          }),
          catchError(() => {
            return this.integrationService
              .getIntegrationById({ organizationId: this.organizationId ?? '' })
              .pipe(
                catchError(() => of(null)),
                switchMap(integration =>
                  this.fetchCustomersFromIds(integration?.customerIds || []).pipe(
                    map(customers => ({
                      integrations: [] as Integration[],
                      customers,
                      parentIntegration: null
                    }))
                  )
                )
              );
          }),
          shareReplay(1)
        );

      customers = dataSource.pipe(
        map(result => result.customers),
        catchError(() => {
          this.buildToastMessage('Failed to load customer data', 'error');
          return of([]);
        })
      );

      integrations = dataSource.pipe(
        map(result => result.integrations)
      );

      parentIntegrations = dataSource.pipe(
        map(result => result.parentIntegration ? [result.parentIntegration] : [])
      );

      employees = this.employeeService
        .getEmployeesByOrganization({
          organizationId: this.organizationId ?? '',
        })
        .pipe(
          catchError((err) => {
            this.buildToastMessage('Failed to load third-party employee data', 'error');
            return of([]);
          })
        );
    }

    forkJoin({
      customers: customers,
      integrations: integrations,
      employees: employees,
      parentIntegrations: parentIntegrations,
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(({ customers, integrations, employees, parentIntegrations }) => {
        if (customers) {
          const { total, active, disabled } = this.countDataItems(customers);
          this.customerCardData.set([
            {
              label: 'Active Customers',
              readonly: {
                title: `${active}`,
              },
            },
            {
              label: 'Disabled Customers',
              readonly: {
                title: `${disabled}`,
              },
            },
            {
              label: 'Total Customers',
              readonly: {
                title: `${total}`,
              },
            },
          ]);
        }
        if (integrations) {
          const { total, active, disabled } = this.countDataItems(integrations);
          this.integrationsCardData.set([
            {
              label: 'Active Third-Party Integrations',
              readonly: {
                title: `${active}`,
              },
            },
            {
              label: 'Disabled Third-Party Integrations',
              readonly: {
                title: `${disabled}`,
              },
            },
            {
              label: 'Total Third-Party Integrations',
              readonly: {
                title: `${total}`,
              },
            },
          ]);
        }

        if (employees) {
          const { total, active, disabled } = this.countDataItems(employees);
          this.employeeCardData.set([
            {
              label: 'Active Third-Party Employees',
              readonly: {
                title: `${active}`,
              },
            },
            {
              label: 'Disabled Third-Party Employees',
              readonly: {
                title: `${disabled}`,
              },
            },
            {
              label: 'Total Third-Party Employees',
              readonly: {
                title: `${total}`,
              },
            },
          ]);
        }

        if (parentIntegrations) {
          const { total, active, disabled } = this.countDataItems(parentIntegrations);
          this.parentIntegrationsCardData.set([
            {
              label: 'Active Third Parties',
              readonly: {
                title: `${active}`,
              },
            },
            {
              label: 'Disabled Third Parties',
              readonly: {
                title: `${disabled}`,
              },
            },
            {
              label: 'Total Third Parties',
              readonly: {
                title: `${total}`,
              },
            },
          ]);
        }
        this.cards = [
          {
            id: 1,
            cardInformation: this.customerCardInformation,
            cardData: this.customerCardData(),
            cardActions: this.customerCardActions,
          },
          {
            id: 3,
            cardInformation: this.employeeCardInformation,
            cardData: this.employeeCardData(),
            cardActions: this.employeeCardActions,
          },
        ];

        if (this.organization === 'assetworks-employee') {
          // Add is the right-hand button, so it is primary and See becomes outlined.
          this.customerCardActions[0].buttonCallback!.buttonType = 'outlined';
          this.employeeCardActions[0].buttonCallback!.buttonType = 'outlined';
          this.customerCardActions.push({
            buttonCallback: {
              label: 'Add Customer',
              buttonType: 'primary',
              action: () => {
                this.router.navigate(['/customer-management/add-customer']);
              },
            },
          });

          this.employeeCardActions.push({
            buttonCallback: {
              label: 'Add Third-Party Employee',
              buttonType: 'primary',
              action: () => {
                this.router.navigate(['/employee-management/add-employee']);
              },
            },
          });

          this.cards.push({
            id: 2,
            cardInformation: this.integrationsCardInformation,
            cardData: this.integrationsCardData(),
            cardActions: this.integrationsCardActions,
          });

          this.cards.push({
            id: 4,
            cardInformation: this.parentIntegrationsCardInformation,
            cardData: this.parentIntegrationsCardData(),
            cardActions: this.parentIntegrationsCardActions,
          });
        }

        if (this.organization === 'integration-employee' && integrations.length > 0) {
          this.cards.splice(1, 0, {
            id: 2,
            cardInformation: this.integrationsCardInformation,
            cardData: this.integrationsCardData(),
            cardActions: [{
              buttonCallback: {
                label: 'See Third-Party Integrations',
                buttonType: 'primary',
                action: () => {
                  this.router.navigate(['/integration-management/overview']);
                },
              },
            }],
          });
        }

        if (this.organization === 'integration-employee' && parentIntegrations.length > 0) {
          const parentOrgId = parentIntegrations[0]?.organizationId;
          
          if (parentOrgId) {
            this.cards.push({
              id: 4,
              cardInformation: this.parentIntegrationsCardInformation,
              cardData: this.parentIntegrationsCardData(),
              cardActions: [{
                buttonCallback: {
                  label: 'See Third Party',
                  buttonType: 'primary',
                  action: () => {
                    this.router.navigate([
                      '/parent-integration-management/parent-integration-details',
                      parentOrgId
                    ]);
                  },
                },
              }],
            });
          }
        }
      });
  }

  drop(
    event:
      | CdkDragDrop<string[]>
      | { previousIndex: number; currentIndex: number }
  ) {
    moveItemInArray(this.cards, event.previousIndex, event.currentIndex);
  }

  integrationsCardInformation: MetricCardInformation = {
    title: 'Third-Party Integration Management',
    altTextColor: false,
  };

  integrationsCardActions: MetricCardAction[] = [
    {
      buttonCallback: {
        label: 'See Third-Party Integrations',
        buttonType: 'outlined',
        action: () => {
          this.router.navigate(['/integration-management/overview']);
        },
      },
    },
    {
      buttonCallback: {
        label: 'Add Third-Party Integration',
        buttonType: 'primary',
        action: () => {
          this.router.navigate(['/integration-management/add-integration']);
        },
      },
    },
  ];

  employeeCardInformation: MetricCardInformation = {
    title: 'Third-Party Employee Management',
    altTextColor: false,
  };

  employeeCardActions: MetricCardAction[] = [
    {
      buttonCallback: {
        label: 'See Third-Party Employees',
        buttonType: 'primary',
        action: () => {
          this.router.navigate(['/employee-management/overview']);
        },
      },
    },
  ];

  customerCardInformation: MetricCardInformation = {
    title: 'Customer Management',
    altTextColor: false,
  };

  customerCardActions: MetricCardAction[] = [
    {
      buttonCallback: {
        label: 'See Customers',
        buttonType: 'primary',
        action: () => {
          this.router.navigate(['/customer-management/overview']);
        },
      },
    },
  ];

  parentIntegrationsCardInformation: MetricCardInformation = {
    title: 'Third-Party Management',
    altTextColor: false,
  };

  parentIntegrationsCardActions: MetricCardAction[] = [
    {
      buttonCallback: {
        label: 'See Third Parties',
        buttonType: 'outlined',
        action: () => {
          this.router.navigate(['/parent-integration-management/overview']);
        },
      },
    },
    {
      buttonCallback: {
        label: 'Add Third Party',
        buttonType: 'primary',
        action: () => {
          this.router.navigate(['/parent-integration-management/add-parent-integration']);
        },
      },
    },
  ];
}
